import React, { useEffect, useState } from 'react';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { EmailEditor } from './EmailEditor';
import { emailApi } from '../../lib/api';
import { toast } from 'sonner';
import { Loader2, Send, Users, AlertTriangle, ExternalLink } from 'lucide-react';

/**
 * Mail-Composer für Sammel-/Einzel-Versand an Seminar-Teilnehmer.
 *
 * Props:
 *  - open, onOpenChange
 *  - seminar: { id, title, survey_link }
 *  - recipients: [{ booking_id, participant_name, participant_email }]
 *  - onSent?: () => void
 */
export function ParticipantMailComposer({ open, onOpenChange, seminar, recipients = [], onSent }) {
    const [templates, setTemplates] = useState([]);
    const [templateId, setTemplateId] = useState('');
    const [subject, setSubject] = useState('');
    const [bodyHtml, setBodyHtml] = useState('');
    const [includeSurvey, setIncludeSurvey] = useState(false);
    const [issueCertificates, setIssueCertificates] = useState(false);
    const [sending, setSending] = useState(false);
    const [sendingTest, setSendingTest] = useState(false);
    const [lastReport, setLastReport] = useState(null);
    const hasSurveyLink = !!(seminar && (seminar.survey_link || '').trim());

    useEffect(() => {
        if (!open) return;
        emailApi.listTemplates('participant')
            .then(r => setTemplates(r.data || []))
            .catch(() => setTemplates([]));
    }, [open]);

    useEffect(() => {
        if (open) {
            // Reset state bei jedem Öffnen
            setTemplateId('');
            setSubject('');
            setBodyHtml('');
            setIncludeSurvey(false);
            setIssueCertificates(false);
            setLastReport(null);
        }
    }, [open]);

    const applyTemplate = (id) => {
        setTemplateId(id);
        const t = templates.find(x => x.id === id);
        if (t) {
            setSubject(t.subject || '');
            setBodyHtml(t.body_html || '');
            // Auto-Aktivierung: Sobald eine Bescheinigungs-/Lizenz-Mail-Vorlage
            // gewählt wird, soll der Bescheinigungs-Schalter automatisch an sein,
            // damit der Admin den Workflow nicht versehentlich überspringt.
            const tt = (t.template_type || '').toLowerCase();
            const looksLikeCertificate =
                tt.includes('certificate') ||
                tt.includes('license') ||
                tt.includes('bescheinigung') ||
                tt.includes('lizenz');
            if (looksLikeCertificate) setIssueCertificates(true);
        }
    };

    const handleSendTest = async () => {
        if (!(subject || '').trim() || !(bodyHtml || '').trim()) {
            toast.error('Betreff und Inhalt sind Pflicht für die Testmail');
            return;
        }
        setSendingTest(true);
        try {
            const t = templates.find(x => x.id === templateId);
            const r = await emailApi.sendTest({
                subject,
                body_html: bodyHtml,
                include_survey_link: includeSurvey && hasSurveyLink,
                survey_intro_html: (t && t.survey_intro_html) || '',
            });
            toast.success(r.data?.message || 'Testmail versendet');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Testmail fehlgeschlagen');
        } finally {
            setSendingTest(false);
        }
    };

    const handleSend = async () => {
        if (!(subject || '').trim() || !(bodyHtml || '').trim()) {
            toast.error('Betreff und Inhalt sind Pflicht');
            return;
        }
        if (!recipients.length) {
            toast.error('Keine Empfänger ausgewählt');
            return;
        }
        setSending(true);
        try {
            const res = await emailApi.sendParticipantBulk({
                seminar_id: seminar.id,
                booking_ids: recipients.map(r => r.booking_id),
                subject,
                body_html: bodyHtml,
                include_survey_link: includeSurvey,
                template_id: templateId || null,
                template_type: templateId ? (templates.find(x => x.id === templateId)?.template_type || 'participant_bulk') : 'participant_bulk',
                issue_certificates: issueCertificates,
            });
            const d = res.data || {};
            const skipped = d.skipped || [];
            const errors = d.errors || [];
            if (skipped.length || errors.length) {
                setLastReport({ sent: d.sent || 0, total: d.total || 0, skipped, errors });
                toast.warning(d.message || `${d.sent || 0} Mails versendet, ${skipped.length} übersprungen`);
            } else {
                toast.success(d.message || `${d.sent || 0} Mails versendet`);
                onOpenChange(false);
            }
            onSent?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Versand fehlgeschlagen');
        } finally {
            setSending(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="participant-mail-composer">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Send className="h-4 w-4" />
                        Mail an Teilnehmer – {seminar?.title}
                    </DialogTitle>
                </DialogHeader>

                <div className="flex flex-col gap-5 py-2">
                    {/* Recipients */}
                    <div className="p-3 bg-muted/40 rounded-lg border" data-testid="composer-recipients">
                        <div className="flex items-center gap-2 text-sm font-medium mb-2">
                            <Users className="h-4 w-4" />
                            Empfänger ({recipients.length})
                        </div>
                        {recipients.length === 0 ? (
                            <p className="text-xs text-muted-foreground italic">Keine Empfänger ausgewählt.</p>
                        ) : (
                            <div className="flex flex-wrap gap-1.5 max-h-24 overflow-y-auto">
                                {recipients.map(r => (
                                    <span key={r.booking_id} className="inline-flex items-center gap-1.5 bg-white border border-gray-200 rounded-full px-2.5 py-1 text-xs">
                                        <span className="font-medium">{r.participant_name || r.participant_email}</span>
                                        <span className="text-muted-foreground">· {r.participant_email}</span>
                                    </span>
                                ))}
                            </div>
                        )}
                        <p className="text-xs text-muted-foreground mt-2">
                            Jeder Empfänger erhält eine eigene E-Mail. Der Versand wird im Profil des jeweiligen Teilnehmers protokolliert.
                        </p>
                    </div>

                    {/* Template loader */}
                    <div className="space-y-2">
                        <Label>Teilnehmer-Vorlage laden</Label>
                        <Select value={templateId || 'none'} onValueChange={(v) => v === 'none' ? setTemplateId('') : applyTemplate(v)}>
                            <SelectTrigger data-testid="composer-template-select">
                                <SelectValue placeholder="– Vorlage auswählen (optional) –" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="none">– Leere Mail –</SelectItem>
                                {templates.map(t => (
                                    <SelectItem key={t.id} value={t.id} data-testid={`composer-template-${t.template_type}`}>
                                        {t.name}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        {templates.length === 0 && (
                            <p className="text-xs text-muted-foreground">
                                Noch keine Teilnehmer-Vorlagen. Unter <strong>Einstellungen → E-Mail-Vorlagen</strong> anlegen
                                (Kategorie = Teilnehmer).
                            </p>
                        )}
                    </div>

                    {/* Subject */}
                    <div className="space-y-2">
                        <Label>Betreff *</Label>
                        <Input
                            value={subject}
                            onChange={(e) => setSubject(e.target.value)}
                            placeholder="z.B. Ihre Anmeldung zu {{seminar_titel}}"
                            data-testid="composer-subject"
                        />
                        <p className="text-xs text-muted-foreground">
                            Platzhalter: {'{{teilnehmer_vorname}}, {{seminar_titel}}, {{seminar_datum}}, {{kursnummer}}, {{portal_url}}, {{bescheinigung_link}}, {{umfrage_block_html}}'}
                        </p>
                    </div>

                    {/* Body */}
                    <div className="space-y-2">
                        <Label>Inhalt *</Label>
                        <EmailEditor
                            content={bodyHtml}
                            onChange={setBodyHtml}
                        />
                    </div>

                    {/* Bescheinigungs-Switch */}
                    <div className={`p-4 rounded-lg border ${issueCertificates ? 'bg-emerald-50/50 border-emerald-200' : 'bg-muted/30 border-border'}`}>
                        <label className="flex items-start gap-3 cursor-pointer">
                            <Switch
                                checked={issueCertificates}
                                onCheckedChange={setIssueCertificates}
                                data-testid="composer-issue-certificates-toggle"
                            />
                            <div className="flex-1">
                                <p className="font-medium text-sm">Bescheinigung erzeugen, anhängen & im Mitgliederprofil verknüpfen</p>
                                <p className="text-xs text-muted-foreground mt-1">
                                    Pro Empfänger wird die im Seminar hinterlegte Bescheinigung bzw. Lizenz erzeugt, der Mail
                                    angehängt, in den Dropbox-Ordner gelegt und beim Mitglied verbucht (UE-Gutschrift / 1.-Hilfe-Nachweis / Lizenz).
                                </p>
                                <p className="text-xs text-amber-700 mt-2 flex items-start gap-1">
                                    <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" />
                                    Es werden <strong>nur Teilnehmer berücksichtigt, die anwesend waren und bezahlt haben</strong>.
                                    Übersprungene erscheinen im Versand-Report.
                                </p>
                            </div>
                        </label>
                    </div>

                    {/* Survey toggle */}
                    <div className={`p-4 rounded-lg border ${includeSurvey ? 'bg-blue-50/50 border-blue-200' : 'bg-muted/30 border-border'}`}>
                        <label className="flex items-start gap-3 cursor-pointer">
                            <Switch
                                checked={includeSurvey}
                                onCheckedChange={setIncludeSurvey}
                                disabled={!hasSurveyLink}
                                data-testid="composer-survey-toggle"
                            />
                            <div className="flex-1">
                                <p className="font-medium text-sm">Umfrage-/Feedback-Link einfügen</p>
                                {hasSurveyLink ? (
                                    <div className="text-xs text-muted-foreground mt-1 space-y-1">
                                        <p>
                                            Ein konfigurierbarer Intro-Text + CTA-Button zum Umfrage-Link wird in die Mail eingefügt
                                            (an Stelle des <code className="bg-gray-100 px-1">{'{{umfrage_block_html}}'}</code>-Platzhalters,
                                            sonst am Ende).
                                        </p>
                                        <p className="flex items-center gap-1">
                                            Link: <a href={seminar.survey_link} target="_blank" rel="noopener noreferrer" className="text-primary underline inline-flex items-center gap-1">
                                                {seminar.survey_link} <ExternalLink className="h-3 w-3" />
                                            </a>
                                        </p>
                                    </div>
                                ) : (
                                    <p className="text-xs text-amber-700 mt-1 flex items-start gap-1">
                                        <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" />
                                        Für dieses Seminar ist noch kein Umfrage-Link hinterlegt. Setzen Sie diesen in den
                                        Seminar-Stammdaten, um den Toggle zu aktivieren.
                                    </p>
                                )}
                            </div>
                        </label>
                    </div>
                </div>

                {lastReport && (
                    <div className="border rounded-lg p-4 bg-muted/20 space-y-3" data-testid="composer-send-report">
                        <p className="text-sm font-medium">Versand-Report</p>
                        <p className="text-xs text-muted-foreground">
                            <strong>{lastReport.sent}</strong> von <strong>{lastReport.total}</strong> Empfängern erhielten eine Mail.
                            {lastReport.skipped.length > 0 && <> {lastReport.skipped.length} übersprungen.</>}
                            {lastReport.errors.length > 0 && <> {lastReport.errors.length} Fehler.</>}
                        </p>
                        {lastReport.skipped.length > 0 && (
                            <div className="space-y-1">
                                <p className="text-xs font-semibold text-amber-700">Übersprungen:</p>
                                <ul className="text-xs text-amber-700 space-y-0.5 max-h-32 overflow-y-auto pl-4 list-disc" data-testid="composer-skipped-list">
                                    {lastReport.skipped.map((s, i) => (
                                        <li key={i}>
                                            <span className="font-medium">{s.name || s.email || s.booking_id}</span>
                                            <span className="text-muted-foreground"> – {s.reason}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        {lastReport.errors.length > 0 && (
                            <div className="space-y-1">
                                <p className="text-xs font-semibold text-red-700">Versandfehler:</p>
                                <ul className="text-xs text-red-700 space-y-0.5 max-h-32 overflow-y-auto pl-4 list-disc">
                                    {lastReport.errors.map((e, i) => (<li key={i}>{e}</li>))}
                                </ul>
                            </div>
                        )}
                    </div>
                )}

                <DialogFooter className="gap-2 sm:gap-2">
                    <Button variant="outline" onClick={handleSendTest} disabled={sendingTest || sending} data-testid="composer-send-test-btn" title="Sendet eine Test-Mail mit Beispieldaten an Ihre Admin-E-Mail-Adresse">
                        {sendingTest ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
                        Testmail an mich
                    </Button>
                    <div className="flex-1" />
                    <Button variant="outline" onClick={() => onOpenChange(false)} disabled={sending || sendingTest}>
                        Abbrechen
                    </Button>
                    <Button onClick={handleSend} disabled={sending || sendingTest || recipients.length === 0} data-testid="composer-send-btn">
                        {sending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
                        {sending ? 'Versende …' : `${recipients.length} Mail${recipients.length === 1 ? '' : 's'} senden`}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default ParticipantMailComposer;
